import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { persistInviteToken } from "@/lib/invites";
import { useGoogleSignInEnabled } from "@/lib/google_auth_enabled";
import { toast } from "sonner";
import { Storefront, CheckCircle, XCircle, EnvelopeSimple, User } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const { user, refresh } = useAuth();
  const googleEnabled = useGoogleSignInEnabled();

  const [state, setState] = useState("loading"); // loading | valid | invalid | accepting | accepted
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); setErr("Missing invitation token"); return; }
    (async () => {
      try {
        const r = await api.get(`/invitations/verify/${encodeURIComponent(token)}`);
        setInfo(r.data); setState("valid");
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invitation is no longer valid");
        setState("invalid");
      }
    })();
  }, [token]);

  const accept = async () => {
    setState("accepting");
    try {
      await api.post("/invitations/accept", { token });
      await refresh();
      setState("accepted");
      toast.success("Invitation accepted — welcome to the team");
      setTimeout(() => nav("/"), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not accept invitation";
      setErr(detail);
      setState("valid");
      toast.error(detail);
    }
  };

  const continueWithLogin = () => {
    persistInviteToken(token);
    nav(`/login?invite=${encodeURIComponent(token)}`);
  };
  const continueWithSignup = () => {
    persistInviteToken(token);
    const e = info?.email ? `&email=${encodeURIComponent(info.email)}` : "";
    nav(`/signup?invite=${encodeURIComponent(token)}${e}`);
  };
  const continueWithGoogle = () => {
    persistInviteToken(token);
    const redirectUrl = window.location.origin + "/auth/google/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const Frame = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center bg-[var(--sand-50)] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-md bg-[var(--terracotta)] flex items-center justify-center">
            <Storefront size={20} color="#fff" weight="fill" />
          </div>
          <div className="font-heading font-semibold text-lg">Margin Mate One POS</div>
        </div>
        <div className="bg-white border border-[rgba(28,25,23,0.08)] rounded-2xl p-8" data-testid="accept-invite-card">
          {children}
        </div>
      </div>
    </div>
  );

  if (state === "loading") {
    return (
      <Frame>
        <div className="flex items-center justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-[var(--sand-100)] border-t-[var(--terracotta)] animate-spin" />
        </div>
      </Frame>
    );
  }

  if (state === "invalid") {
    return (
      <Frame>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--destructive-bg)] flex items-center justify-center mx-auto mb-4">
            <XCircle size={36} weight="fill" className="text-[var(--destructive)]" />
          </div>
          <div className="label-eyebrow mb-2 text-[var(--destructive)]">Invitation invalid</div>
          <h1 className="font-heading text-xl font-semibold mb-2" data-testid="invite-invalid-title">Can't use this link</h1>
          <p className="text-sm text-[var(--ink-500)] mb-6">{err || "This invitation may be expired, revoked, or already used."}</p>
          <Link to="/login" data-testid="invite-back-to-login" className="inline-flex h-11 px-6 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] font-medium items-center">Go to sign in</Link>
        </div>
      </Frame>
    );
  }

  if (state === "accepted") {
    return (
      <Frame>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--moss-bg)] flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={36} weight="fill" className="text-[var(--moss)]" />
          </div>
          <div className="label-eyebrow mb-2">You're in</div>
          <h1 className="font-heading text-xl font-semibold mb-2">Welcome to {info?.business_name}</h1>
          <p className="text-sm text-[var(--ink-500)]">Redirecting to your dashboard…</p>
        </div>
      </Frame>
    );
  }

  // state === "valid"
  return (
    <Frame>
      <div className="label-eyebrow mb-2">You've been invited</div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1" data-testid="invite-business-name">
        Join {info?.business_name}
      </h1>
      <p className="text-sm text-[var(--ink-500)] mb-5">
        as <span className="font-medium text-[var(--ink-700)] capitalize" data-testid="invite-role">{info?.role}</span>
        {info?.venue_name && <> at {info.venue_name}</>}
      </p>

      <div className="bg-[var(--sand-100)]/40 border border-[rgba(28,25,23,0.08)] rounded-md p-3 mb-5 text-sm flex items-center gap-2">
        <EnvelopeSimple size={16} className="text-[var(--ink-500)]" />
        <span className="text-[var(--ink-700)] tabular" data-testid="invite-email">{info?.email}</span>
      </div>

      {user ? (
        (user.email || "").toLowerCase() === (info?.email || "").toLowerCase() ? (
          <>
            <p className="text-sm text-[var(--ink-500)] mb-4">
              You're signed in as <span className="font-medium">{user.name}</span>. Accept this invitation to join the team.
            </p>
            <button
              onClick={accept}
              disabled={state === "accepting"}
              data-testid="invite-accept-btn"
              className="w-full h-11 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] active:bg-[var(--terracotta-darker)] active:scale-[0.99] text-white font-medium transition-all duration-200 disabled:opacity-60"
            >
              {state === "accepting" ? "Accepting…" : "Accept invitation"}
            </button>
          </>
        ) : (
          <div className="rounded-md border border-[rgba(28,25,23,0.12)] p-4 text-sm">
            <div className="flex items-center gap-2 mb-2 text-[var(--destructive)]">
              <XCircle size={16} weight="fill" /> <span className="font-medium">Email mismatch</span>
            </div>
            <p className="text-[var(--ink-500)] mb-3">
              You're signed in as <span className="font-mono">{user.email}</span> but this invitation was issued to <span className="font-mono">{info?.email}</span>. Sign out and sign in with the right account.
            </p>
            <Link to="/login" className="inline-flex h-9 px-4 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] text-sm font-medium items-center">Sign out & switch</Link>
          </div>
        )
      ) : (
        <div className="space-y-2">
          <button
            onClick={continueWithLogin}
            data-testid="invite-login-btn"
            className="w-full h-11 rounded-md bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] active:scale-[0.99] text-white font-medium transition-all"
          >
            <span className="inline-flex items-center justify-center gap-2"><User size={16} /> Sign in to accept</span>
          </button>
          <button
            onClick={continueWithSignup}
            data-testid="invite-signup-btn"
            className="w-full h-11 rounded-md border border-[rgba(28,25,23,0.12)] bg-white hover:bg-[var(--sand-100)] text-sm font-medium"
          >
            Create an account with this email
          </button>
          {googleEnabled && (
          <button
            type="button"
            onClick={continueWithGoogle}
            data-testid="invite-google-btn"
            className="w-full h-11 rounded-md border border-[rgba(28,25,23,0.12)] bg-white hover:bg-[var(--sand-100)] text-sm font-medium flex items-center justify-center gap-2"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC04" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>
          )}
        </div>
      )}
    </Frame>
  );
}
